// DOM kit — the web counterpart of scripts/lib/kit/. Hyperscript el(),
// vsj-wired buttons (debounce, deny reason, glow), bars with the hp chip
// machine, tooltip singleton, modals, progress animator.

import { sendBtn, serverNow } from './net';
import * as sfx from './audio/sfx';
import type { MaybeBtn } from './types';

// ---------- hyperscript ----------
type Child = Node | string | null | undefined | false;

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, any> = {},
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v == null || v === false) continue;
    if (k === 'class') node.className = String(v);
    else if (k === 'style') node.setAttribute('style', String(v));
    else if (k === 'dataset') Object.assign(node.dataset, v);
    else if (k.startsWith('on') && typeof v === 'function')
      node.addEventListener(k.slice(2).toLowerCase(), v);
    else if (k === 'text') node.textContent = String(v);
    else node.setAttribute(k, String(v));
  }
  for (const c of children) {
    if (c == null || c === false) continue;
    node.append(c instanceof Node ? c : document.createTextNode(c));
  }
  return node;
}

export function clear(node: HTMLElement): HTMLElement {
  node.replaceChildren();
  return node;
}

// ---------- deny handling ----------
let lastDenyAt = 0;
let denyAlert: (text: string) => void = () => {};
export function onDenyAlert(fn: (text: string) => void): void {
  denyAlert = fn;
}

export function denyPress(node: HTMLElement, reason?: string | null): void {
  sfx.playEvent('deny_soft');
  node.classList.remove('deny-shake');
  void node.offsetWidth; // restart animation
  node.classList.add('deny-shake');
  const now = Date.now();
  if (reason && now - lastDenyAt > 2500) {
    lastDenyAt = now;
    denyAlert(reason);
  }
}

// ---------- button ----------
const DEBOUNCE_MS = 250;

export interface KBtnOpts {
  label?: string;
  compact?: boolean;
  danger?: boolean;
  active?: boolean;
  glow?: boolean;
  denyReason?: string | null;
  overrides?: () => Record<string, unknown>;
  cls?: string;
  title?: string;
  onFired?: () => void;
  /** SOUNDS event for the press ('' = silent; default ui_tick). */
  clickSound?: string;
}

/** A vsj-wired button: null btn = disabled (deny-shake + optional reason). */
export function kbtn(btn: MaybeBtn, opts: KBtnOpts = {}): HTMLButtonElement {
  const b = el('button', {
    class:
      'k-btn' +
      (opts.compact ? ' compact' : '') +
      (opts.danger ? ' danger' : '') +
      (opts.active ? ' active' : '') +
      (opts.glow ? ' glow' : '') +
      (opts.cls ? ' ' + opts.cls : ''),
    title: opts.title,
  });
  b.textContent = opts.label ?? '';
  let lastFire = 0;
  if (!btn) {
    b.disabled = true;
    // guidance glow still renders on disabled widgets (a grayed control
    // points). Disabled buttons are inert to events — src/interact.ts finds
    // them by hit-test and reads the reason off the dataset.
    if (opts.denyReason) b.dataset.denyReason = opts.denyReason;
    b.addEventListener('pointerdown', () => denyPress(b, opts.denyReason));
  } else {
    b.addEventListener('click', () => {
      const now = Date.now();
      if (now - lastFire < DEBOUNCE_MS) return;
      lastFire = now;
      const cue = opts.clickSound ?? 'ui_tick';
      if (cue) sfx.playEvent(cue); // volume comes from the SOUNDS tuning table
      sendBtn(btn, opts.overrides ? opts.overrides() : {});
      opts.onFired?.();
    });
  }
  return b;
}

/** Arm-to-confirm destructive button ("Delete" -> "Sure?" for 2.5s). */
export function armBtn(btn: MaybeBtn, label: string, opts: KBtnOpts = {}): HTMLButtonElement {
  let armed = false;
  let timer = 0;
  const b = el('button', {
    class: 'k-btn danger' + (opts.compact ? ' compact' : ''),
    text: label,
  });
  if (!btn) {
    b.disabled = true;
    return b;
  }
  b.addEventListener('click', () => {
    sfx.playEvent('ui_tick');
    if (!armed) {
      armed = true;
      b.textContent = 'Sure?';
      timer = window.setTimeout(() => {
        armed = false;
        b.textContent = label;
      }, 2500);
      return;
    }
    clearTimeout(timer);
    sendBtn(btn, opts.overrides ? opts.overrides() : {});
    armed = false;
    b.textContent = label;
  });
  return b;
}

// ---------- bar ----------
export interface KBar {
  root: HTMLElement;
  set(frac: number, label?: string): void;
  setHp(cur: number, max: number): void;
  flash(): void;
}

/** Bar with stoneheim's two-layer hp machine (curAct.js hpBar, ported
    verbatim): a HIT drops the solid fill at once and leaves the chip behind
    at the old height, easing down — the pale red overhang is what was just
    lost; a HEAL (animateGain bars — the player's) jumps the chip up at once
    and eases the fill up into it — the bright green overhang is what is
    coming in. One easing rule on whichever layer trails: 22% of the
    remaining gap per 100ms step, snap inside 0.4% (stoneheim curAct.js
    ease() + balance.js TICK_MS = 100). */
const CHIP_EASE = 0.22;
const CHIP_SNAP = 0.004;
const CHIP_STEP_MS = 100;

export function kbar(kind = '', height?: number, animateGain = false): KBar {
  const chip = el('div', { class: 'chip' });
  const fill = el('div', { class: 'fill' });
  const label = el('div', { class: 'label' });
  const root = el('div', { class: `k-bar ${kind}` }, chip, fill, label);
  if (height) root.style.height = `${height}px`;
  let cur = 0;
  let chipFrac = 0;
  let fillFrac = 0;
  let bound = false; // first setHp of a fight binds clean (stoneheim null)
  let timer = 0;
  const ease = (from: number, to: number) =>
    Math.abs(to - from) < CHIP_SNAP ? to : from + (to - from) * CHIP_EASE;
  const paint = () => {
    chip.style.width = `${chipFrac * 100}%`;
    fill.style.width = `${fillFrac * 100}%`;
    // colour the overhang by the direction it is moving
    chip.classList.toggle('healing', fillFrac < cur - 0.0001);
  };
  const step = () => {
    // chip leads on a gain, trails on a loss; fill does the opposite
    chipFrac = cur >= chipFrac ? cur : ease(chipFrac, cur);
    fillFrac = !animateGain || cur <= fillFrac ? cur : ease(fillFrac, cur);
    paint();
    timer = chipFrac !== cur || fillFrac !== cur ? window.setTimeout(step, CHIP_STEP_MS) : 0;
  };
  return {
    root,
    set(frac: number, text?: string) {
      cur = chipFrac = fillFrac = Math.max(0, Math.min(1, frac));
      bound = true;
      clearTimeout(timer);
      timer = 0;
      paint();
      if (text !== undefined) label.textContent = text;
    },
    setHp(hp: number, max: number) {
      const frac = max > 0 ? Math.max(0, Math.min(1, hp / max)) : 0;
      label.textContent = `${hp} / ${max}`;
      if (!bound) {
        bound = true;
        cur = chipFrac = fillFrac = frac;
        paint();
        return;
      }
      if (frac === cur) return;
      cur = frac;
      clearTimeout(timer);
      step();
    },
    flash() {
      root.classList.remove('bar-flash');
      void root.offsetWidth;
      root.classList.add('bar-flash');
    },
  };
}

// ---------- timed progress (ends_at driven, server clock) ----------
interface TimedBar {
  bar: KBar;
  endsAt: number;
  cycleMs: number;
  startedAt?: number;
}
const timedBars = new Set<TimedBar>();

export function timedBar(bar: KBar, endsAt: number, cycleMs: number, startedAt?: number): () => void {
  const t: TimedBar = { bar, endsAt, cycleMs, startedAt };
  timedBars.add(t);
  return () => timedBars.delete(t);
}

function tickTimed(): void {
  const now = serverNow() + 100; // 0.1s lead per protocol
  for (const t of timedBars) {
    if (!t.bar.root.isConnected) {
      timedBars.delete(t);
      continue;
    }
    if (t.cycleMs <= 0) continue;
    const remain = t.endsAt - now;
    const frac = 1 - Math.max(0, Math.min(1, remain / t.cycleMs));
    t.bar.set(frac);
  }
  requestAnimationFrame(tickTimed);
}
requestAnimationFrame(tickTimed);

// ---------- tooltip singleton ----------
let tipNode: HTMLElement | null = null;
let tipTimer = 0;

export function tooltip(target: HTMLElement, content: () => string | Node | null): void {
  target.addEventListener('pointerenter', (e) => {
    tipTimer = window.setTimeout(() => {
      const c = content();
      if (c == null) return;
      hideTip();
      tipNode = el('div', { class: 'k-tooltip' });
      tipNode.append(c instanceof Node ? c : document.createTextNode(c));
      document.body.append(tipNode);
      positionTip(e as PointerEvent);
    }, 450);
  });
  target.addEventListener('pointermove', (e) => tipNode && positionTip(e));
  target.addEventListener('pointerleave', hideTip);
  target.addEventListener('pointerdown', hideTip);
}

function positionTip(e: PointerEvent): void {
  if (!tipNode) return;
  const pad = 12;
  const r = tipNode.getBoundingClientRect();
  let x = e.clientX + pad;
  let y = e.clientY + pad;
  if (x + r.width > innerWidth - 4) x = e.clientX - r.width - pad;
  if (y + r.height > innerHeight - 4) y = e.clientY - r.height - pad;
  tipNode.style.left = `${x}px`;
  tipNode.style.top = `${y}px`;
}

function hideTip(): void {
  clearTimeout(tipTimer);
  tipNode?.remove();
  tipNode = null;
}

// ---------- modal ----------
export interface ModalHandle {
  scrim: HTMLElement;
  body: HTMLElement;
  close(): void;
}

export function modal(opts: { dismissable?: boolean; onClose?: () => void } = {}): ModalHandle {
  const body = el('div', { class: 'k-modal' });
  const scrim = el('div', { class: 'k-scrim' }, body);
  const close = () => {
    scrim.remove();
    opts.onClose?.();
  };
  if (opts.dismissable) {
    scrim.addEventListener('click', (e) => {
      if (e.target === scrim) close();
    });
  }
  document.getElementById('overlays')!.append(scrim);
  return { scrim, body, close };
}

// ---------- misc ----------
export function flashClass(node: Element, cls: string): void {
  node.classList.remove(cls);
  void (node as HTMLElement).offsetWidth;
  node.classList.add(cls);
}

export function fmtDur(s: number): string {
  if (s < 90) return `${Math.round(s * 10) / 10}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${Math.round(s - m * 60)}s`;
}
